import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Button, Card, CardContent, CircularProgress, TextField, Typography, Alert,
} from '@mui/material';
import { getReferrerByCode, submitReferral } from '../../api/referralLinkApi';

/**
 * What a referrer's customer sees when they tap /r/{code}.
 *
 * Public: whoever opens this has no account and never will. It asks for the least that
 * lets somebody ring them back — a name and a phone — and says plainly who sent them,
 * because a form that appears out of nowhere asking for a phone number is one nobody
 * fills in.
 *
 * (user 2026-10-18: "they can refer their customer as a link aso. referral can refer as
 * a link to their own customer")
 */

const FONT = "'Poppins', sans-serif";

export default function ReferralLandingPage() {
    const { code } = useParams();

    const [referrer, setReferrer] = useState(null);
    // The salesperson behind this link (the referrer's upline) — for the WhatsApp
    // button. Null when they have no upline, and the button is then not drawn.
    const [salesName, setSalesName] = useState('');
    const [salesPhone, setSalesPhone] = useState('');
    const [loading, setLoading] = useState(true);
    const [badLink, setBadLink] = useState(false);

    // Name and phone only. The other three were dropped on 2026-10-31; Submit still
    // accepts them, so nothing on the API side had to change.
    const [form, setForm] = useState({ name: '', phone: '' });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        getReferrerByCode(code)
            .then(res => {
                if (!alive) return;
                setReferrer(res?.data?.referrerName || 'our team');
                setSalesName(res?.data?.salesName || '');
                setSalesPhone(res?.data?.salesPhone || '');
            })
            .catch(() => { if (alive) setBadLink(true); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [code]);

    const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

    const canSend = form.name.trim().length >= 2
        && form.phone.replace(/\D/g, '').length >= 8
        && !sending;

    const send = async () => {
        setSending(true);
        setError('');
        try {
            await submitReferral({ code, ...form });
            setDone(true);
        } catch (err) {
            // The server says what is wrong — a short name, a phone that is not one —
            // so show that rather than a blanket failure the person cannot act on.
            const d = err?.response?.data;
            setError(typeof d === 'string' && d.trim() ? d : 'Could not send that. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const shell = (children) => (
        <Box sx={{
            minHeight: '100vh', background: '#F8FAFC',
            display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
        }}>
            <Card sx={{ width: '100%', maxWidth: 440, borderRadius: 3 }}>
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>{children}</CardContent>
            </Card>
        </Box>
    );

    if (loading) return shell(<Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>);

    if (badLink) {
        return shell(
            <>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>
                    This link isn’t working
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: '#64748B' }}>
                    Ask whoever sent it to share it again — it may have been copied only partly.
                </Typography>
            </>
        );
    }

    /* The salesperson's WhatsApp, built once and used on both the form and the thank-you
       screen. 016-939 9511 → 60169399511: wa.me takes digits only, with the leading 0
       swapped for the country code. */
    const waHref = salesPhone
        ? `https://wa.me/${String(salesPhone).replace(/\D/g, '').replace(/^0/, '60')}?text=${encodeURIComponent(
            `Hi${salesName ? ' ' + salesName : ''}, I got your details from ${referrer} about solar.`)}`
        : null;
    const waButton = (sx) => (waHref ? (
        <Button fullWidth variant="outlined" component="a" href={waHref} target="_blank" rel="noopener noreferrer"
            sx={{ fontFamily: FONT, fontWeight: 700, py: 1.1, color: '#15803D', borderColor: '#86EFAC',
                  '&:hover': { borderColor: '#15803D', bgcolor: 'rgba(22,163,74,0.06)' }, ...sx }}>
            WhatsApp Sales{salesPhone ? ` · ${salesPhone}` : ''}
        </Button>
    ) : null);

    if (done) {
        return shell(
            <>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.2rem', mb: 1 }}>
                    Thank you — we’ll be in touch
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: '#64748B', mb: 2.5 }}>
                    One of our consultants will call you about solar for your place. {referrer} will
                    know you got in touch.
                </Typography>

                {/* Waiting for a call is not everyone's preference, and somebody who has just
                    typed their number is the most willing they will ever be to talk.
                    (user 2026-10-31: "yes" to showing it after submit) */}
                {waButton()}

                {/* One link, many introductions. A referrer sharing this in a group chat
                    should not need a fresh link for the second person who fills it in.
                    (user 2026-10-31: "referral can send again") */}
                <Button fullWidth variant="text"
                    onClick={() => { setForm({ name: '', phone: '' }); setError(''); setDone(false); }}
                    sx={{ fontFamily: FONT, fontWeight: 600, mt: 1, color: '#64748B' }}>
                    Send another person's details
                </Button>
            </>
        );
    }

    return shell(
        <>
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.25rem', color: '#0F172A', mb: 0.5 }}>
                {referrer} thinks solar suits your place
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', color: '#64748B', mb: 2.5 }}>
                Leave your name and number and we’ll work out what your roof could save you.
                No cost, no obligation.
            </Typography>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <TextField fullWidth size="small" label="Your name" value={form.name}
                onChange={set('name')} sx={{ mb: 1.5 }} />
            <TextField fullWidth size="small" label="Phone number" value={form.phone}
                onChange={set('phone')} sx={{ mb: 1.5 }} inputProps={{ inputMode: 'tel' }} />
            {/* NAME AND NUMBER, NOTHING ELSE. Email, location and a free-text note were
                asked for and are gone: every extra box on a stranger's form is another
                reason to close the tab, and the team rings these people anyway — the
                rest is quicker to ask on the call than to type on a phone.
                (user 2026-10-31: "btm 3 one donnid. only name, phone") */}

            {/* The grey disabled state now comes from the theme (MuiButton.containedPrimary),
                so every unclickable button in the app looks unclickable. Nothing needed here. */}
            <Button fullWidth variant="contained" disabled={!canSend} onClick={send}
                sx={{ fontFamily: FONT, fontWeight: 700, py: 1.2, mt: 1 }}>
                {sending ? 'Sending…' : 'Send my details'}
            </Button>

            {/* STRAIGHT TO A HUMAN. Some people will not leave their number on a form but
                will happily message, so the salesperson who handles this referrer's leads
                is one tap away. The number is their upline's; absent one, this does not
                render rather than pointing nowhere. (user 2026-10-31) */}
            {waButton({ mt: 1.25 })}

            <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', color: '#94A3B8', mt: 2, textAlign: 'center' }}>
                We use your details only to contact you about this enquiry.
            </Typography>
        </>
    );
}
